import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NotificationManager } from "react-notifications";
import BtButton from "../bt-button/bt-button";
import { Loading } from "../../components/shared";
import TooltipContainer from "../../components/shared/TooltipContainer/TooltipContainer";
import { placeOptions } from "../app-place-checklist/placeOptions";
import serviceSales, { TedCategory } from "../../services/service-sales";
import { serviceTenderWatch, TenderWatchFilter } from "../../services/service-tender-watch";
import styles from "./app-tender-watch-panel.module.scss";

type Area = { _id: string, name: string };

/** Beyond this the row stops being readable and becomes a wall; the rest go in a tooltip. */
const MAX_VISIBLE_CPV_CODES = 20;

const placeLabel = (value: string) => placeOptions.find((option) => option.value === value)?.label ?? value;

type Props = {
  // Without the private tab there are no published requests and no presentation to fall back on.
  showsPrivate?: boolean,
};

const AppTenderWatchPanel: FC<Props> = ({ showsPrivate = true }) => {
  // Rendered only on the profile page, so its copy lives beside the toggles it sits with.
  const [t] = useTranslation("profile", { keyPrefix: "bevakning" });
  const [loading, setLoading] = useState(true);
  const [watch, setWatch] = useState<TenderWatchFilter | null>(null);
  const [areas, setAreas] = useState<Area[]>([]);
  const [tedCategories, setTedCategories] = useState<TedCategory[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Areas name every category; the TED list additionally carries the CPV codes each one
      // stands for, which is what a saved category actually matches on.
      const [saved, superAdminAreas, categories] = await Promise.all([
        serviceTenderWatch.get(),
        serviceSales.fetchSuperAdminAreas(),
        serviceSales.fetchTedCategories().catch(() => [] as TedCategory[]),
      ]);
      setWatch(saved?.isSaved ? saved : null);
      setAreas(superAdminAreas ?? []);
      setTedCategories(categories ?? []);
    } catch (error) {
      NotificationManager.error(t("notification.fetchError", "Kunde inte hämta din bevakning"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const remove = useCallback(async () => {
    const confirmed = window.confirm(showsPrivate
      ? t("panel.deleteConfirm", "Ta bort bevakningen? Veckomailet utgår då från företagets leverantörspresentation igen.")
      : t("panel.deleteConfirmPublicTenders", "Ta bort bevakningen? Du får då inget veckomail om offentliga upphandlingar."));
    if (!confirmed) return;

    try {
      await serviceTenderWatch.remove();
      setWatch(null);
      NotificationManager.success(t("notification.deleted", "Bevakningen är borttagen"));
    } catch (error) {
      NotificationManager.error(t("notification.deleteError", "Kunde inte ta bort bevakningen"));
    }
  }, [t, showsPrivate]);

  if (loading) return <Loading type="inline" />;

  if (!watch) {
    return (
      <p className={styles.empty}>
        {showsPrivate
          ? t(
            "panel.empty",
            "Du har ingen egen bevakning, så veckomailet utgår från företagets leverantörspresentation. Spara en filtrering på Publicerade förfrågningar för att styra den själv.",
          )
          : t(
            "panel.emptyPublicTenders",
            "Du har ingen bevakning än. Spara en filtrering under Offentliga upphandlingar i menyn för att få ett veckomail med dem som matchar.",
          )}
      </p>
    );
  }

  const categoryNames = watch.areaIds
    .map((id: string) => areas.find((area) => area._id === String(id))?.name)
    .filter(Boolean) as string[];

  // A saved category stands for the CPV codes it maps to, and those are what a notice is
  // actually matched against. Showing only the category name hides what is being watched.
  const codesFromCategories = new Set<string>();
  watch.areaIds.forEach((id: string) => {
    tedCategories
      .find((category) => category.id === String(id))
      ?.cpvCodes.forEach((code) => codesFromCategories.add(code));
  });
  watch.cpvCodes.forEach((code) => codesFromCategories.add(code));
  const cpvCodes = Array.from(codesFromCategories).sort();

  const visibleCpvCodes = cpvCodes.slice(0, MAX_VISIBLE_CPV_CODES);
  const hiddenCpvCodes = cpvCodes.slice(MAX_VISIBLE_CPV_CODES);

  // An empty dimension does not narrow anything, so it has to read as "all of them". Showing
  // an empty row instead is the thing that made this unclear: nothing looks like a mistake.
  const simpleRows = [
    {
      key: "categories",
      label: t("panel.categories", "Kategorier"),
      values: categoryNames,
      all: t("panel.allCategories", "Alla kategorier"),
    },
    {
      key: "places",
      label: t("panel.places", "Regioner"),
      values: watch.places.map(placeLabel),
      all: t("panel.allPlaces", "Hela Sverige"),
    },
  ];

  // Category and region are the only dimensions the private marketplace understands. Naming
  // neither says nothing about it, and the digest then leaves that half to the company's
  // supplier presentation rather than matching none of it.
  const constrainsPrivate = watch.areaIds.length > 0 || watch.places.length > 0;

  return (
    <div className={styles.panel}>
      <p className={styles.summary}>
        {!showsPrivate
          ? t("panel.summaryPublicTenders", "Du får de offentliga upphandlingar som matchar allt nedan.")
          : constrainsPrivate
          ? t("panel.summary", "Du får de förfrågningar och offentliga upphandlingar som matchar allt nedan.")
          : t(
            "panel.summaryPublicOnly",
            "Du får de offentliga upphandlingar som matchar allt nedan. Förfrågningar på marknadsplatsen följer företagets leverantörspresentation.",
          )}
      </p>

      <dl className={styles.rows}>
        {simpleRows.map((row) => (
          <div className={styles.row} key={row.key}>
            <dt className={styles.label}>{row.label}</dt>
            <dd className={styles.value}>
              {row.values.length > 0
                ? row.values.map((value) => (
                  <span className={styles.chip} key={value}>{value}</span>
                ))
                : <span className={styles.any}>{row.all}</span>}
            </dd>
          </div>
        ))}

        <div className={styles.row}>
          <dt className={styles.label}>{t("panel.cpvCodes", "CPV-koder")}</dt>
          <dd className={styles.value}>
            {cpvCodes.length === 0 && (
              <span className={styles.any}>{t("panel.noCpvCodes", "Inga valda")}</span>
            )}
            {visibleCpvCodes.map((code) => (
              <span className={styles.chip} key={code}>{code}</span>
            ))}
            {hiddenCpvCodes.length > 0 && (
              <TooltipContainer renderReferenceComponent={(className, ref) => (
                <span className={`${styles.chip} ${styles.more} ${className}`} ref={ref}>
                  {t("panel.moreCpvCodes", "+{{count}}", { count: hiddenCpvCodes.length })}
                </span>
              )}>
                <div className={styles.tooltip}>{hiddenCpvCodes.join(", ")}</div>
              </TooltipContainer>
            )}
          </dd>
        </div>
      </dl>

      <div className={styles.actions}>
        <BtButton color="white" size="small" onClick={remove}>
          {t("panel.delete", "Ta bort bevakningen")}
        </BtButton>
      </div>
    </div>
  );
};

export default AppTenderWatchPanel;
